<template>
  <Modal
    :title="$t('contract.actions.editServiceContract')"
    ref="editServiceContractItemModal"
    name="EDIT_ITEM_SERVICE_VALUES"
  >
    <template v-slot:body>
      <v-form ref="addServiceValuesForm" v-model="valid">
        <v-autocomplete
          v-model="type"
          :no-data-text="$t('contract.noData')"
          :label="$t('contract.label.serviceType')"
          :items="serviceTypes"
          :rules="[rules.required]"
          :item-text="(item) => $t(`type.${item.value}`)"
          :hide-selected="false"
          item-value="value"
          hide-details="auto"
          single-line
          clearable
          outlined
          disabled
        />

        <h3 class="mt-8">
          {{ $t('contract.label.values') }}
        </h3>

        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.lazy="price"
              v-money="catalogCurrency()"
              :label="$t('contract.label.price')"
              :rules="[rules.required]"
              class="amount inputAmount mr-2"
              hide-details="auto"
              ref="price"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="discount"
              :amount="discount"
              :label="$t('contract.label.discount')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.lazy="square_meter_price"
              v-money="catalogCurrency()"
              :label="$t('contract.label.squareMetersPrice')"
              :rules="[rules.required]"
              class="amount inputAmount mr-2"
              hide-details="auto"
              ref="square_meter_price"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="base_square_meters_size"
              :label="$t('contract.label.baseSquareMetersPrice')"
              :rules="[rules.custom]"
              class="amount inputAmount mr-2"
              hide-details="auto"
              suffix="m²"
              outlined
            />
          </v-col>
        </v-row>

        <h3 class="mt-6 mb-3">
          {{ $t('defaultServiceValues.taxes') }}
        </h3>

        <v-row>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="host_tax"
              :amount="host_tax"
              :label="$t('contract.label.taxHost')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="displacement_tax"
              :amount="displacement_tax"
              :label="$t('contract.label.displacementTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="square_meter_tax"
              :amount="square_meter_tax"
              :label="$t('contract.label.taxSquareMeters')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="furniture_tax"
              :amount="furniture_tax"
              :label="$t('contract.label.furnitureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="semi_furniture_tax"
              :amount="semi_furniture_tax"
              :label="$t('contract.label.semiFurnitureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="house_tax"
              :amount="house_tax"
              :label="$t('contract.label.houseTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="trousseau_tax"
              :amount="trousseau_tax"
              :label="$t('contract.label.trousseauTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="host_realty_feature_tax"
              :amount="host_realty_feature_tax"
              :label="$t('contract.label.hostRealtyFeatureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              :label="$t('contract.label.urgencyTax')"
              v-model="urgency_tax"
              :amount="urgency_tax"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              :label="$t('contract.label.hostUrgencyTax')"
              :amount="host_urgency_tax"
              v-model="host_urgency_tax"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
              :disabled="urgency_tax <= 0"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="urgency_hours_limit"
              :label="$t('contract.label.urgencyHoursLimit')"
              class="amount inputAmount mr-2"
              :rules="urgency_tax > 0 ? [rules.required] : []"
              hide-details="auto"
              suffix="h"
              outlined
              :disabled="urgency_tax <= 0"
            />
          </v-col>
        </v-row>

        <v-spacer class="my-5" />

        <div class="d-flex justify-end">
          <v-btn
            @click="updateValue"
            color="primary"
            :disabled="!valid"
            elevation="0"
            x-large
          >
            {{ $t('contract.actions.editService') }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import { amount, percent, required } from '@/util/errorHandling'
import { clearCurrency, checkCurrencyDecimals } from '@/util/helpers'
import { catalogCurrency } from '@/util/catalog'
import { mapActions } from 'vuex'
import { VMoney } from 'v-money'
import HAmount from '../../app/HAmount.vue'

export default {
  name: 'EditServicesValues',

  components: {
    Modal,
    HAmount,
  },

  props: {
    serviceTypes: {
      required: true
    }
  },

  directives: {
    money: VMoney
  },

  watch: {
    urgency_tax(tax) {
      if (!tax || tax <= 0) {
        this.host_urgency_tax = 0
        this.urgency_hours_limit = null
      }
    }
  },

  methods: {
    ...mapActions(['closeModal', 'openModal']),

    openEditModal(serviceContract) {
      if (serviceContract) {


        this.serviceContract = serviceContract
        this.openModal('EDIT_ITEM_SERVICE_VALUES')
      }
    },

    close() {
      this.closeModal('EDIT_ITEM_SERVICE_VALUES')
    },

    resetValue() {
      this.price = 0

      const price = this.$refs?.price?.$refs?.input
      price.value = '0.00'
    },

    sanitizeServiceContract() {
      this.serviceContract = {
        type: this.type,
        price: this.price,
        discount: this.discount,
        square_meter_price: this.square_meter_price,
        base_square_meters_size: String(this.base_square_meters_size).replace(',', '.'),
        host_tax: this.host_tax,
        displacement_tax: this.displacement_tax,
        square_meter_tax: this.square_meter_tax,
        furniture_tax: this.furniture_tax,
        semi_furniture_tax: this.semi_furniture_tax,
        house_tax: this.house_tax,
        trousseau_tax: this.trousseau_tax,
        host_realty_feature_tax: this.host_realty_feature_tax,
        urgency_tax: this.urgency_tax,
        host_urgency_tax: this.host_urgency_tax,
        urgency_hours_limit: this.urgency_hours_limit,
      }
    },

    updateValue() {
      this.sanitizeServiceContract()
      this.$emit('updateServiceContract', this.serviceContract)
      this.close()
    }
  },

  mounted() {
    // focus on select type
    this.$watch(
      () => this.$refs.editServiceContractItemModal.dialog,
      (isOpen) => {
        if (isOpen) {
          this.$nextTick(() => {
            const priceInput = this.$refs?.price?.$refs?.input
            const squareMeterPriceInput = this.$refs?.square_meter_price?.$refs?.input
            const price = /R\$/.test(this.serviceContract.price) ? clearCurrency(this.serviceContract.price) : this.serviceContract.price
            const squareMeterPrice = /R\$/.test(this.serviceContract.square_meter_price) ? clearCurrency(this.serviceContract.square_meter_price) : this.serviceContract.square_meter_price

            this.$nextTick(() => {
              this.type = this.serviceContract.type
              priceInput.value = checkCurrencyDecimals(price)
              this.discount = this.serviceContract.discount ?? '0'
              squareMeterPriceInput.value = checkCurrencyDecimals(squareMeterPrice)
              this.base_square_meters_size = this.serviceContract.base_square_meters_size ?? '0'
              this.host_tax = this.serviceContract.host_tax ?? '0'
              this.displacement_tax = this.serviceContract.displacement_tax ?? '0'
              this.square_meter_tax = this.serviceContract.square_meter_tax ?? '0'
              this.furniture_tax = this.serviceContract.furniture_tax ?? '0'
              this.semi_furniture_tax = this.serviceContract.semi_furniture_tax ?? '0'
              this.house_tax = this.serviceContract.house_tax ?? '0'
              this.trousseau_tax = this.serviceContract.trousseau_tax ?? '0'
              this.host_realty_feature_tax = this.serviceContract.host_realty_feature_tax ?? '0'
              this.urgency_tax = this.serviceContract.urgency_tax ?? '0'
              this.host_urgency_tax = this.serviceContract.host_urgency_tax ?? '0'
              this.urgency_hours_limit = this.serviceContract.urgency_hours_limit
            })
          })
        }
      }
    )
  },

  data() {
    return {
      serviceContract: null,

      type: null,
      price: null,
      discount: null,
      square_meter_price: null,
      base_square_meters_size: null,
      host_tax: null,
      displacement_tax: null,
      square_meter_tax: null,
      furniture_tax: null,
      semi_furniture_tax: null,
      house_tax: null,
      trousseau_tax: null,
      host_realty_feature_tax: null,
      urgency_tax: null,
      host_urgency_tax: null,
      urgency_hours_limit: null,

      rules: {
        amount,
        percent,
        required,
        custom: (value) => (value >= 0) || 'Campo obrigatório'
      },

      valid: false,

      catalogCurrency,
    }
  },
}
</script>
